import React, { useState, useContext, useEffect } from 'react';
import { Modal, Avatar, Button, Input, message, Popconfirm } from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  MoreOutlined, 
  UserDeleteOutlined,
  LogoutOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'context/AppContext';
import { Team } from 'models/CommonType';
import { 
  updateTeamName, 
  updateTeamDescription, 
  joinTeamWithCode, 
  requestToJoinTeam, 
  leaveTeam as leaveTeamAPI, 
  removeTeamMember as removeTeamMemberAPI,
  useTeamDetails,
  useTeamRequests,
  uploadTeamPhoto,
  disbandTeam 
} from 'services/TeamService';
import PhotoUploadModal from './PhotoUploadModal';
import TeamRequestsList from './TeamRequestsList';
import './TeamMemberDetailsModal.css';

interface TeamMemberDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  team: Team;
  authKey: string;
  hasTeam?: boolean;
}

const TeamMemberDetailsModal: React.FC<TeamMemberDetailsModalProps> = ({
  isOpen,
  onClose,
  onBack,
  team,
  authKey,
  hasTeam = false
}) => {
  const { t } = useTranslation();
  const { commonData } = useContext(AppContext);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isPhotoUploadModalOpen, setIsPhotoUploadModalOpen] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // Fetch fresh team details when modal is open
  const { team: freshTeam, refreshTeam } = useTeamDetails(
    team.id,
    authKey,
    isOpen
  );
  
  // Use fresh team data if available, fallback to prop team
  const currentTeam = freshTeam || team;
  const [teamName, setTeamName] = useState(currentTeam.name);
  const [teamDescription, setTeamDescription] = useState(currentTeam.description || '');
  
  // Update local state when fresh team data arrives
  useEffect(() => {
    if (freshTeam) {
      setTeamName(freshTeam.name);
      setTeamDescription(freshTeam.description || '');
    }
  }, [freshTeam]);
  
  // Check if current user is team admin
  const isTeamAdmin = currentTeam.user.id === commonData.userDetails?.id;
  const isTeamMember = currentTeam.members.some(member => member.id === commonData.userDetails?.id);

  // Fetch team requests for admin
  const { 
    requests: teamRequests, 
    isLoading: requestsLoading, 
    hasNextPage, 
    loadNextPage, 
    refreshRequests 
  } = useTeamRequests(
    team.id,
    authKey,
    isOpen && isTeamAdmin && !!currentTeam.code
  );

  const handleSaveTeamName = async () => {
    try {
      await updateTeamName(team.id, teamName, authKey);
      message.success(t('#Teams.team_name_updated'));
      setIsEditingName(false);
      // Refresh team data to show updated information
      refreshTeam();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleSaveDescription = async () => {
    try {
      await updateTeamDescription(team.id, teamDescription, authKey);
      message.success(t('#Teams.team_description_updated'));
      setIsEditingDescription(false);
      // Refresh team data to show updated information
      refreshTeam();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeTeamMemberAPI(team.id, memberId, authKey);
      message.success(t('#Teams.member_removed'));
      // Refresh team data to show updated member list
      refreshTeam();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleLeaveTeam = async () => {
    try {
      await leaveTeamAPI(team.id, authKey);
      message.success(t('#Teams.left_team'));
      onClose();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleJoinTeam = async () => {
    if (!joinCode.trim()) {
      message.error(t('#Teams.enter_valid_code'));
      return;
    }
    
    try {
      await joinTeamWithCode(team.id, joinCode, authKey);
      message.success(t('#Teams.joined_team'));
      onClose();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleRequestToJoin = async () => {
    try {
      await requestToJoinTeam(team.id, authKey);
      message.success(t('#Teams.join_request_sent'));
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleCopyInviteCode = () => {
    if (currentTeam.code) {
      navigator.clipboard.writeText(currentTeam.code);
      message.success(t('#Teams.invite_code_copied'));
    }
  };


  const handlePhotoUpload = async (file: File) => {
    setIsUploadingPhoto(true);
    try {
      await uploadTeamPhoto(team.id, file, authKey);
      // Refresh team data to show updated photo
      refreshTeam();
    } catch (error: any) {
      throw new Error(error.message || t('#Teams.photo_upload_error'));
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDisbandTeam = async () => {
    try {
      await disbandTeam(team.id, authKey);
      message.success(t('#Teams.team_disbanded'));
      // Close the modal and go back to teams list
      onClose();
    } catch (error: any) {
      message.error(error.message || t('#Common.error_occurred'));
    }
  };

  const handleRequestUpdate = () => {
    // Refresh both team data and requests when a request is processed
    refreshTeam();
    refreshRequests();
  };

  const handleMemberClick = (userId: string) => {
    const profileUrl = `${window.location.origin}/profile/${userId}`;
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  const renderTeamHeader = () => (
    <div className="team-header">
      <div className="team-header-top">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="back-button"
        />
        <div className="header-actions">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={() => setShowMoreActions(!showMoreActions)}
            className="more-button"
          />
        </div>
      </div>

      <div className="team-compact-section">
        <div className="team-image-compact">
          <Avatar
            size={50}
            src={currentTeam.image_url}
            className="team-avatar-compact"
          >
            {currentTeam.name[0]?.toUpperCase()}
          </Avatar>
        </div>
        
        <div className="team-info-compact">
          {isEditingName ? (
            <Input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              onPressEnter={handleSaveTeamName}
              onBlur={handleSaveTeamName}
              className="team-name-input-compact"
              autoFocus
              size="small"
            />
          ) : (
            <div className="team-name-compact" onClick={() => isTeamMember && setIsEditingName(true)}>
              <h3 className="team-name-text">{currentTeam.name}</h3>
              {isTeamMember && <EditOutlined className="edit-icon-compact" />}
            </div>
          )}
          <div className="team-member-count">
            {currentTeam.members.length} {currentTeam.members.length === 1 ? 'member' : 'members'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamDescription = () => (
    <div className="team-description-section">
      <h3 className="section-title">{t('#Teams.team_description')}</h3>
      {isEditingDescription ? (
        <div className="edit-description">
          <Input.TextArea
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
            onBlur={handleSaveDescription}
            rows={3}
            className="description-textarea"
            autoFocus
          />
          <div className="description-actions">
            <Button size="small" onClick={() => setIsEditingDescription(false)}>
              {t('#Common.cancel')}
            </Button>
            <Button type="primary" size="small" onClick={handleSaveDescription}>
              {t('#Common.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="description-display" 
          onClick={() => isTeamMember && setIsEditingDescription(true)}
        >
          {teamDescription ? (
            <p className="team-description">{teamDescription}</p>
          ) : (
            <p className="no-description">
              {isTeamMember ? t('#Teams.tap_to_add_description') : t('#Teams.no_description')}
            </p>
          )}
          {isTeamMember && <EditOutlined className="edit-icon" />}
        </div>
      )}
    </div>
  );

  const renderTeamMembers = () => (
    <div className="team-members-section">
      <h3 className="section-title">{t('#Teams.team_members')} ({currentTeam.members.length})</h3>
      <div className="members-list">
        {currentTeam.members.map((member) => (
          <div key={member.id} className="member-item">
            <div className="member-info" onClick={() => member.id && handleMemberClick(member.id)}>
              <Avatar
                size={48}
                src={member.profile?.image_small_url}
                className="member-avatar clickable-avatar"
              >
                {member.first_name?.[0]}{member.last_name?.[0]}
              </Avatar>
              <div className="member-details">
                <div className="member-name clickable-name">
                  {member.first_name} {member.last_name}
                  {member.id === currentTeam.user.id && (
                    <span className="admin-badge">{t('#Teams.admin')}</span>
                  )}
                </div>
                <div className="member-bio">
                  {member.profile?.bio || t('#Teams.no_bio')}
                </div>
              </div>
            </div>
            {isTeamAdmin && member.id !== currentTeam.user.id && member.id && (
              <Popconfirm
                title={t('#Teams.remove_member_confirm')}
                onConfirm={() => handleRemoveMember(member.id!)}
                okText={t('#Common.yes')}
                cancelText={t('#Common.no')}
              >
                <Button
                  type="text"
                  icon={<UserDeleteOutlined />}
                  className="remove-member-icon-button"
                  size="small"
                  title="Remove Member"
                />
              </Popconfirm>
            )}
          </div>
        ))}
      </div>
    </div>
  );


  // iOS-based conditional rendering for bottom view
  const renderBottomView = () => {
    // Team Full Message
    if (currentTeam.isFull) {
      return (
        <div className="team-full-section">
          <div className="team-full-notice">
            <Button disabled className="join-button-disabled" block size="large">
              {t('#Teams.team_full')}
            </Button>
          </div>
        </div>
      );
    }

    // User is team admin/member - show sharing & request management
    if (currentTeam.code !== null && isTeamMember) {
      return (
        <div className="team-actions-section">
          {/* Share & QR Code View */}
          <div className="invite-section-compact">
            <h3 className="section-title">{t('#Teams.invite_friends')}</h3>
            <div className="invite-compact-container">
              <div className="qr-code-compact">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(currentTeam.code || '')}&format=png&margin=10`}
                  alt="Team QR Code"
                  className="qr-code-small"
                />
              </div>
              <div className="invite-code-compact">
                <div className="code-label-small">{t('#Teams.invite_code')}</div>
                <div className="code-container">
                  <span className="code-value-small">{currentTeam.code || ''}</span>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={handleCopyInviteCode}
                    className="copy-code-button-small"
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Leave Team Button (for non-admin members) */}
          {!isTeamAdmin && (
            <div className="member-actions">
              <Popconfirm
                title={t('#Teams.leave_team_confirm')}
                onConfirm={handleLeaveTeam}
                okText={t('#Common.yes')}
                cancelText={t('#Common.no')}
              >
                <Button
                  type="text"
                  icon={<LogoutOutlined />}
                  className="leave-team-button"
                  danger
                  block
                >
                  {t('#Teams.leave_team')}
                </Button>
              </Popconfirm>
            </div>
          )}

          {/* Team Request List (for admin when not full and has requests) */}
          {isTeamAdmin && !currentTeam.isFull && teamRequests.length > 0 && (
            <div className="team-requests-section">
              <TeamRequestsList
                requests={teamRequests}
                authKey={authKey}
                onRequestUpdate={handleRequestUpdate}
                isLoading={requestsLoading}
                hasNextPage={hasNextPage}
                onLoadMore={loadNextPage}
              />
            </div>
          )}
        </div>
      );
    }

    // User not in any team - show join options
    if (!hasTeam && !isTeamMember) {
      return (
        <div className="join-team-section">
          {/* Join Team View with code entry and QR scanner */}
          <div className="join-with-code-section">
            <h3 className="section-title">{t('#Teams.join_with_code')}</h3>
            <div className="join-with-code-compact">
              <Input
                placeholder={t('#Teams.enter_join_code')}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                onPressEnter={handleJoinTeam}
                className="join-code-input-compact"
                size="large"
                suffix={
                  <Button 
                    type="primary" 
                    onClick={handleJoinTeam}
                    className="join-code-button"
                    disabled={!joinCode.trim()}
                  >
                    {t('#Teams.join')}
                  </Button>
                }
              />
            </div>
          </div>

          {/* Request to Join Button */}
          {!currentTeam.isFull && (
            <div className="request-to-join-section">
              <Button 
                type="primary" 
                onClick={handleRequestToJoin}
                className={currentTeam.is_requested ? "join-button-requested" : "join-button-primary"}
                disabled={currentTeam.is_requested}
                block
                size="large"
              >
                {currentTeam.is_requested ? t('#Teams.request_sent') : t('#Teams.request_to_join')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Default: empty state
    return null;
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      className="team-member-details-modal"
      width="90%"
      style={{ maxWidth: 500 }}
      closable={false}
      destroyOnClose
    >
      <div className="team-member-details-content">
        <div className="team-details-scrollable">
          {renderTeamHeader()}
          {renderTeamDescription()}
          {renderTeamMembers()}
          {renderBottomView()}
        </div>
      </div>

      {/* More Actions Menu */}
      {showMoreActions && (
        <div className="more-actions-overlay" onClick={() => setShowMoreActions(false)}>
          <div className="more-actions-menu" onClick={(e) => e.stopPropagation()}>
            {isTeamAdmin && (
              <Popconfirm
                title={t('#Teams.disband_confirm')}
                onConfirm={() => {
                  setShowMoreActions(false);
                  handleDisbandTeam();
                }}
                okText={t('#Common.yes')}
                cancelText={t('#Common.no')}
                placement="top"
              >
                <div className="menu-item danger">
                  <UserDeleteOutlined />
                  {t('#Teams.disband_team')}
                </div>
              </Popconfirm>
            )}
            {isTeamMember && !isTeamAdmin && (
              <Popconfirm
                title={t('#Teams.leave_team_confirm')}
                onConfirm={() => {
                  setShowMoreActions(false);
                  handleLeaveTeam();
                }}
                okText={t('#Common.yes')}
                cancelText={t('#Common.no')}
                placement="top"
              >
                <div className="menu-item danger">
                  <LogoutOutlined />
                  {t('#Teams.leave_team')}
                </div>
              </Popconfirm>
            )}
            {!isTeamMember && (
              <div className="menu-item" onClick={() => console.log('Report team')}>
                <MoreOutlined />
                {t('#Teams.report_team')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={isPhotoUploadModalOpen}
        onClose={() => setIsPhotoUploadModalOpen(false)}
        onPhotoUpload={handlePhotoUpload}
        currentPhotoUrl={currentTeam.image_url}
        title={t('#Teams.change_team_photo')}
        isLoading={isUploadingPhoto}
      />

    </Modal>
  );
};

export default TeamMemberDetailsModal;